<script lang="ts" setup>
import { computed, PropType, ref } from "vue";

interface ChartPoint {
  label: string;
  value: number;
}

const props = defineProps({
  metricLabel: {
    type: String,
    required: true,
  },
  valueFormatter: {
    type: Function as PropType<(value: number) => string>,
    required: true,
  },
  emptyLabel: {
    type: String,
    required: true,
  },
  points: {
    type: Array as PropType<ChartPoint[]>,
    required: true,
  },
  accentClass: {
    type: String,
    default: "tw:text-[var(--classic-accent,var(--tw-color-pv-turquoise-600))]",
  },
});

const width = 680;
const height = 280;
const padding = {
  top: 18,
  right: 20,
  bottom: 40,
  left: 20,
};

const series = computed(() =>
  props.points.map((point, index) => ({
    ...point,
    x:
      props.points.length === 1
        ? width / 2
        : padding.left +
          (index / (props.points.length - 1)) *
            (width - padding.left - padding.right),
  })),
);

const maxValue = computed(() =>
  Math.max(...series.value.map((point) => point.value), 0),
);

const normalizedSeries = computed(() => {
  const innerHeight = height - padding.top - padding.bottom;
  const upperBound = maxValue.value <= 0 ? 1 : maxValue.value;

  return series.value.map((point) => ({
    ...point,
    y: height - padding.bottom - (point.value / upperBound) * innerHeight,
  }));
});

const linePath = computed(() => {
  if (normalizedSeries.value.length === 0) {
    return "";
  }

  return normalizedSeries.value
    .map(
      (point, index) =>
        `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
    )
    .join(" ");
});

const areaPath = computed(() => {
  if (normalizedSeries.value.length === 0) {
    return "";
  }

  const first = normalizedSeries.value[0];
  const last = normalizedSeries.value[normalizedSeries.value.length - 1];

  return [
    `M ${first.x.toFixed(2)} ${(height - padding.bottom).toFixed(2)}`,
    ...normalizedSeries.value.map(
      (point) => `L ${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
    ),
    `L ${last.x.toFixed(2)} ${(height - padding.bottom).toFixed(2)}`,
    "Z",
  ].join(" ");
});

const yTicks = computed(() => {
  const upperBound = maxValue.value <= 0 ? 1 : maxValue.value;

  return [0, 0.33, 0.66, 1].map((fraction) => {
    const value = Math.round(upperBound * fraction);
    const y =
      height -
      padding.bottom -
      fraction * (height - padding.top - padding.bottom);

    return {
      value,
      label: props.valueFormatter(value),
      y,
    };
  });
});

const xTicks = computed(() => {
  if (normalizedSeries.value.length <= 4) {
    return normalizedSeries.value;
  }

  const indexes = [0, 1, 2, 3].map((slot) =>
    Math.round((slot / 3) * (normalizedSeries.value.length - 1)),
  );

  return [...new Set(indexes)].map((index) => normalizedSeries.value[index]);
});

const activePointIndex = ref<number | null>(null);

const activePoint = computed(() =>
  activePointIndex.value === null
    ? null
    : normalizedSeries.value[activePointIndex.value],
);

const hasLowSignalRange = computed(
  () => props.points.length > 0 && maxValue.value <= 5,
);

const chartStrokeWidth = computed(() => (hasLowSignalRange.value ? 2.25 : 3));
const areaTopOpacity = computed(() => (hasLowSignalRange.value ? 0.1 : 0.24));
const activeTooltipStyle = computed(() => {
  if (!activePoint.value) {
    return {};
  }

  return {
    left: `${(activePoint.value.x / width) * 100}%`,
    top: `${(activePoint.value.y / height) * 100}%`,
  };
});

function setActivePoint(index: number) {
  activePointIndex.value = index;
}

function clearActivePoint() {
  activePointIndex.value = null;
}
</script>

<template>
  <div
    class="tw:rounded-2xl tw:border tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:p-5 tw:shadow-[0_16px_48px_-32px_rgba(3,41,71,0.35)]"
  >
    <div class="tw:mb-5 tw:flex tw:items-center tw:justify-between tw:gap-3">
      <div>
        <div
          class="tw:text-[0.72rem] tw:font-semibold tw:uppercase tw:tracking-[0.18em] tw:text-[var(--classic-text-muted,var(--tw-color-gray-400))]"
        >
          {{ metricLabel }}
        </div>
      </div>
    </div>

    <div
      v-if="points.length === 0"
      class="tw:flex tw:h-[280px] tw:items-center tw:justify-center tw:rounded-2xl tw:border tw:border-dashed tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface-raised,var(--tw-color-gray-50))] tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
    >
      {{ emptyLabel }}
    </div>

    <div v-else class="presentation-analytics-chart-frame">
      <svg
        :viewBox="`0 0 ${width} ${height}`"
        class="tw:block tw:h-[280px] tw:w-full"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="presentation-analytics-area"
            x1="0"
            x2="0"
            y1="0"
            y2="1"
          >
            <stop
              offset="0%"
              stop-color="var(--classic-accent, #35B6B9)"
              :stop-opacity="areaTopOpacity"
            />
            <stop
              offset="100%"
              stop-color="var(--classic-accent, #35B6B9)"
              stop-opacity="0.02"
            />
          </linearGradient>
        </defs>

        <g v-for="tick in yTicks" :key="tick.y">
          <line
            :x1="padding.left"
            :x2="width - padding.right"
            :y1="tick.y"
            :y2="tick.y"
            stroke="var(--classic-border-subtle, #E5E7EB)"
            stroke-dasharray="4 6"
          />
          <text
            x="4"
            :y="tick.y - 4"
            fill="var(--classic-text-muted, #9CA3AF)"
            font-size="11"
          >
            {{ tick.label }}
          </text>
        </g>

        <path :d="areaPath" fill="url(#presentation-analytics-area)" />
        <path
          :d="linePath"
          fill="none"
          stroke="var(--classic-accent, #35B6B9)"
          :stroke-width="chartStrokeWidth"
          stroke-linecap="round"
          stroke-linejoin="round"
        />

        <line
          v-if="activePoint"
          :x1="activePoint.x"
          :x2="activePoint.x"
          :y1="padding.top"
          :y2="height - padding.bottom"
          stroke="var(--classic-accent-hover, #8FD2D4)"
          stroke-dasharray="3 5"
        />

        <g
          v-for="(point, index) in normalizedSeries"
          :key="`${point.label}-${point.x}`"
        >
          <circle
            class="presentation-analytics-chart-hit-area"
            :cx="point.x"
            :cy="point.y"
            r="13"
            fill="transparent"
            @mouseenter="setActivePoint(index)"
            @mouseleave="clearActivePoint"
          />
          <circle
            :cx="point.x"
            :cy="point.y"
            :r="activePointIndex === index ? 6 : 4.5"
            fill="var(--classic-surface, white)"
            stroke="var(--classic-accent, #35B6B9)"
            stroke-width="2"
            pointer-events="none"
          />
        </g>

        <g v-for="tick in xTicks" :key="`${tick.label}-${tick.x}`">
          <text
            :x="tick.x"
            :y="height - 10"
            fill="var(--classic-text-muted, #9CA3AF)"
            font-size="11"
            text-anchor="middle"
          >
            {{ tick.label }}
          </text>
        </g>
      </svg>

      <div
        v-if="activePoint"
        class="presentation-analytics-chart-tooltip"
        :style="activeTooltipStyle"
      >
        <div class="presentation-analytics-chart-tooltip__label">
          {{ activePoint.label }}
        </div>
        <div class="presentation-analytics-chart-tooltip__value">
          {{ valueFormatter(activePoint.value) }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.presentation-analytics-chart-frame {
  position: relative;
}

.presentation-analytics-chart-hit-area {
  cursor: pointer;
}

.presentation-analytics-chart-tooltip {
  position: absolute;
  z-index: 2;
  min-width: 132px;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--classic-border-subtle, rgba(203, 213, 225, 0.88));
  border-radius: 0.9rem;
  color: var(--classic-text, #111827);
  background: var(--classic-tooltip-surface, rgba(255, 255, 255, 0.94));
  box-shadow: 0 18px 38px -26px rgba(3, 41, 71, 0.65);
  pointer-events: none;
  transform: translate(-50%, calc(-100% - 0.75rem));
  backdrop-filter: blur(10px);
}

.presentation-analytics-chart-tooltip__label {
  color: var(--classic-text-muted, #667085);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.presentation-analytics-chart-tooltip__value {
  margin-top: 0.2rem;
  color: var(--classic-text, #0f172a);
  font-size: 1rem;
  font-weight: 750;
}
</style>
